import React, { useState, useEffect } from 'react';
import { ArrowLeft, Facebook, CheckCircle, Settings, Zap, RefreshCw, Filter, Plus, X, Loader2, Lock } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import { API_URL } from '../../config';



export default function CommentMonitoring({ onBack }) {
  const [step, setStep] = useState(1);
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [posts, setPosts] = useState([]);
  const [selectedPosts, setSelectedPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState({
    autoReply: true,
    customMessage: 'Salama Tompoko! Manasa anao hifandray aminay ato {messenger_link}',
    filters: []
  });

  useEffect(() => {
    loadPages();
  }, []);

  const loadPages = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/clients`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // ⭐ FILTRER : seulement actives
        const activePages = data.clients.filter(c => 
          c.status === 'active' && c.owner_user_id !== null
        );
        
        setPages(activePages);
      }
    } catch (error) {
      toast.error('Erreur chargement pages');
    }
    setLoading(false);
  };

  const loadPosts = async (pageId) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/clients/${pageId}/posts`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPosts(data.posts || []);
      }
    } catch (error) {
      toast.error('Erreur chargement publications');
    }
    setLoading(false);
  };

  const refreshPosts = async () => {
    if (!selectedPage) return;
    
    toast.loading('Actualisation depuis Facebook...', { id: 'refresh' });
    
    try {
      const response = await fetch(`${API_URL}/api/clients/${selectedPage.$id}/activate`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPosts(data.posts || []);
        toast.success('Publications actualisées !', { id: 'refresh' });
      } else {
        toast.error('Erreur actualisation', { id: 'refresh' });
      }
    } catch (error) {
      toast.error('Erreur réseau', { id: 'refresh' });
    }
  };

  const activateMonitoring = async () => {
    if (selectedPosts.length === 0) {
      toast.error('Sélectionnez au moins une publication');
      return;
    }
  
    setLoading(true);
    toast.loading('Activation en cours...', { id: 'activate' });
  
    try {
      const response = await fetch(`${API_URL}/api/clients/${selectedPage.$id}/start-monitoring`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          post_ids: selectedPosts,
          config: config 
        })
      });
  
      if (response.ok) {
        toast.success('Surveillance activée !', { id: 'activate' });
        setTimeout(() => onBack(), 2000);
      } else {
        toast.error('Erreur activation', { id: 'activate' });
      }
    } catch (error) {
      toast.error('Erreur réseau', { id: 'activate' });
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 p-4 md:p-8">
      <Toaster position="top-right" />
      
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="w-12 h-12 rounded-xl bg-white/10 hover:bg-white/20 transition-all flex items-center justify-center"
          >
            <ArrowLeft className="w-6 h-6 text-white" />
          </button>
          
          <div className="flex-1">
            <h2 className="text-3xl font-bold text-white">Surveillance Commentaires</h2>
            <p className="text-blue-200">Configuration et activation du service</p>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="grid grid-cols-4 gap-2">
          {['Pages', 'Publications', 'Configuration', 'Activation'].map((label, idx) => (
            <div key={idx} className="space-y-2">
              <div className={`h-2 rounded-full transition-all ${
                step > idx + 1 ? 'bg-green-500' : step === idx + 1 ? 'bg-blue-500' : 'bg-white/20'
              }`} />
              <div className={`text-sm text-center ${step === idx + 1 ? 'text-white font-semibold' : 'text-blue-300'}`}>
                {label}
              </div>
            </div>
          ))}
        </div>

        {/* Step 1: Pages */}
        {step === 1 && (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-white">Sélectionnez une page</h3>
            
            {loading ? (
              <div className="text-center py-12">
                <Loader2 className="w-12 h-12 text-blue-400 animate-spin mx-auto" />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {pages.map((page) => (
                  <div
                    key={page.$id}
                    onClick={() => {
                      setSelectedPage(page);
                      loadPosts(page.$id);
                      setStep(2);
                    }}
                    className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:bg-white/10 cursor-pointer transition-all group"
                  >
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                        <Facebook className="w-8 h-8 text-white" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-xl font-bold text-white">{page.facebook_page_name}</h4>
                        <p className="text-sm text-blue-200">{page.name}</p>
                      </div>
                    </div>
                    
                    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
                      page.status === 'active' 
                        ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                        : 'bg-gray-500/20 text-gray-300 border border-gray-500/30'
                    }`}>
                      <div className={`w-2 h-2 rounded-full ${page.status === 'active' ? 'bg-green-400 animate-pulse' : 'bg-gray-400'}`} />
                      {page.status === 'active' ? 'Active' : 'Inactive'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 2: Posts */}
        {step === 2 && (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <h3 className="text-2xl font-bold text-white">Sélectionnez les publications</h3>
      <button
        onClick={refreshPosts}
        className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-xl text-white transition-all"
      >
        <RefreshCw className="w-4 h-4" />
        Actualiser
      </button>
    </div>

    {loading ? (
      <div className="text-center py-12">
        <Loader2 className="w-12 h-12 text-blue-400 animate-spin mx-auto" />
      </div>
    ) : (
      <div className="space-y-4">
        {posts.map((post) => {
          const isAlreadyMonitored = post.is_monitored;
          const isDisabled = isAlreadyMonitored || (selectedPosts.length >= 10 && !selectedPosts.includes(post.$id));
          
          return (
            <div
              key={post.$id}
              onClick={() => {
                if (isDisabled) {
                  if (isAlreadyMonitored) {
                    toast.error('Cette publication est déjà surveillée');
                  } else {
                    toast.error('Maximum 10 publications');
                  }
                  return;
                }
                
                if (selectedPosts.includes(post.$id)) {
                  setSelectedPosts(selectedPosts.filter(id => id !== post.$id));
                } else {
                  setSelectedPosts([...selectedPosts, post.$id]);
                }
              }}
              className={`bg-white/5 backdrop-blur-xl border-2 rounded-2xl p-6 transition-all ${
                isAlreadyMonitored
                  ? 'border-yellow-500/50 bg-yellow-500/5 cursor-not-allowed opacity-60'
                  : selectedPosts.includes(post.$id)
                  ? 'border-blue-500 bg-blue-500/10 cursor-pointer hover:scale-[1.02]'
                  : 'border-white/10 hover:border-white/20 cursor-pointer'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  isAlreadyMonitored
                    ? 'border-yellow-500 bg-yellow-500/20'
                    : selectedPosts.includes(post.$id)
                    ? 'border-blue-500 bg-blue-500'
                    : 'border-white/30'
                }`}>
                  {isAlreadyMonitored ? (
                    <Lock className="w-4 h-4 text-yellow-400" />
                  ) : selectedPosts.includes(post.$id) ? (
                    <CheckCircle className="w-4 h-4 text-white" />
                  ) : null}
                </div>
                
                <div className="flex-1">
                  <p className="text-white mb-3 line-clamp-2">{post.post_content}</p>
                  <div className="flex items-center gap-4 text-sm text-blue-200 mb-2">
                    <span>{new Date(post.post_date).toLocaleDateString()}</span>
                    <span>❤️ {post.likes_count}</span>
                    <span>💬 {post.comments_count}</span>
                  </div>
                  
                  {isAlreadyMonitored && (
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs bg-yellow-500/20 text-yellow-300 border border-yellow-500/30">
                      <CheckCircle className="w-3 h-3" />
                      Déjà surveillée
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    )}

            <div className="flex justify-between">
              <button
                onClick={() => setStep(1)}
                className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-xl text-white transition-all"
              >
                Retour
              </button>
              <button
                onClick={() => setStep(3)}
                disabled={selectedPosts.length === 0}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-xl text-white transition-all"
              >
                Suivant ({selectedPosts.length} sélectionnées)
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Configuration */}
        {step === 3 && (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-white">Configuration</h3>

            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 space-y-6">
              {/* Auto Reply Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-white font-semibold mb-1">Réponse automatique</h4>
                  <p className="text-sm text-blue-200">Activer les réponses aux commentaires</p>
                </div>
                <button
                  onClick={() => setConfig({...config, autoReply: !config.autoReply})}
                  className={`w-14 h-8 rounded-full transition-all ${
                    config.autoReply ? 'bg-green-500' : 'bg-gray-600'
                  }`}
                >
                  <div className={`w-6 h-6 bg-white rounded-full transition-transform ${
                    config.autoReply ? 'translate-x-7' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              {/* Custom Message */}
              <div>
                <label className="block text-white font-semibold mb-2">Message personnalisé</label>
                <textarea
                  value={config.customMessage}
                  onChange={(e) => setConfig({...config, customMessage: e.target.value})}
                  className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  rows="3"
                  placeholder="Message par défaut..."
                />
                <p className="text-xs text-blue-300 mt-2">
                  Utilisez {'{messenger_link}'} pour insérer le lien Messenger
                </p>
              </div>

              {/* Filters */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-white font-semibold">Filtres intelligents</h4>
                  <button
                    onClick={() => setConfig({
                      ...config,
                      filters: [...config.filters, { keywords: '', message: '' }]
                    })}
                    className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm transition-all"
                  >
                    <Plus className="w-4 h-4" />
                    Ajouter filtre
                  </button>
                </div>

                <div className="space-y-3">
                  {config.filters.map((filter, idx) => (
                    <div key={idx} className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-blue-200 text-sm">Filtre #{idx + 1}</span>
                        <button
                          onClick={() => setConfig({
                            ...config,
                            filters: config.filters.filter((_, i) => i !== idx)
                          })}
                          className="text-red-400 hover:text-red-300"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      
                      <input
                        type="text"
                        value={filter.keywords}
                        onChange={(e) => {
                          const newFilters = [...config.filters];
                          newFilters[idx].keywords = e.target.value;
                          setConfig({...config, filters: newFilters});
                        }}
                        className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Mots-clés (ex: prix, achat, commander)"
                      />
                      
                      <textarea
                        value={filter.message}
                        onChange={(e) => {
                          const newFilters = [...config.filters];
                          newFilters[idx].message = e.target.value;
                          setConfig({...config, filters: newFilters});
                        }}
                        className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                        rows="2"
                        placeholder="Message spécifique pour ces mots-clés..."
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <button
                onClick={() => setStep(2)}
                className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-xl text-white transition-all"
              >
                Retour
              </button>
              <button
                onClick={() => setStep(4)}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-xl text-white transition-all"
              >
                Suivant
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Activation */}
        {step === 4 && (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-white">Récapitulatif et Activation</h3>

            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 space-y-6">
              {/* Résumé */}
              <div className="space-y-4">
                <div>
                  <h4 className="text-blue-200 text-sm mb-1">Page sélectionnée</h4>
                  <p className="text-white font-semibold">{selectedPage?.facebook_page_name}</p>
                </div>

                <div>
                  <h4 className="text-blue-200 text-sm mb-1">Publications surveillées</h4>
                  <p className="text-white font-semibold">{selectedPosts.length} publication(s)</p>
                </div>

                <div>
                  <h4 className="text-blue-200 text-sm mb-1">Réponse automatique</h4>
                  <p className="text-white font-semibold">{config.autoReply ? 'Activée' : 'Désactivée'}</p>
                </div>

                {config.filters.length > 0 && (
                  <div>
                    <h4 className="text-blue-200 text-sm mb-1">Filtres configurés</h4>
                    <p className="text-white font-semibold">{config.filters.length} filtre(s)</p>
                  </div>
                )}
              </div>

              {/* Bouton Activation */}
              <button
                onClick={activateMonitoring}
                disabled={loading}
                className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white py-4 rounded-xl font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-3"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Activation en cours...
                  </>
                ) : (
                  <>
                    <Zap className="w-5 h-5" />
                    Activer la surveillance
                  </>
                )}
              </button>
            </div>

            <button
              onClick={() => setStep(3)}
              disabled={loading}
              className="w-full px-6 py-3 bg-white/10 hover:bg-white/20 rounded-xl text-white transition-all disabled:opacity-50"
            >
              Retour
            </button>
          </div>
        )}
      </div>
    </div>
  );
}